import { User } from '@auth/user';
import { PartialDeep } from 'type-fest';
import { apiClient } from '@/api/client';
import {
	clearAuthStorage as clearDualAuthStorage,
	getAuthItem,
	isPersistentSession,
	setAuthItem
} from '@auth/authStorage';

function getErrorStatus(error: unknown): number | undefined {
	if (typeof error !== 'object' || error === null || !('status' in error)) {
		return undefined;
	}

	const { status } = error as { status?: unknown };

	return typeof status === 'number' ? status : undefined;
}

/**
 * Clear corrupted auth data from BOTH localStorage and sessionStorage.
 * Re-exported for compatibility with existing imports.
 */
export function clearAuthStorage() {
	clearDualAuthStorage();
}

// Clear corrupted data on module load (browser only). Check both stores because, depending
// on the user's Remember Me choice, the session lives in one or the other.
if (typeof window !== 'undefined') {
	try {
		const storedUser = window.sessionStorage.getItem('loggedUser') ?? window.localStorage.getItem('loggedUser');

		if (storedUser && storedUser !== 'undefined' && storedUser !== 'null') {
			JSON.parse(storedUser);
		} else if (storedUser === 'undefined' || storedUser === 'null') {
			console.error('Detected corrupted auth storage data, clearing...');
			clearAuthStorage();
		}
	} catch (error) {
		console.error('Detected corrupted auth storage data, clearing...', error);
		clearAuthStorage();
	}
}

/**
 * Retry a function with exponential backoff
 */
async function retryWithBackoff<T>(fn: () => Promise<T>, maxRetries = 2, baseDelay = 1000): Promise<T> {
	let lastError: unknown;

	for (let attempt = 0; attempt <= maxRetries; attempt += 1) {
		try {
			return await fn();
		} catch (error: unknown) {
			lastError = error;

			// Don't retry on authentication errors (401, 403)
			const status = getErrorStatus(error);

			if (status === 401 || status === 403) {
				throw error;
			}

			if (attempt < maxRetries) {
				const delay = baseDelay * Math.pow(2, attempt);

				await new Promise((resolve) => setTimeout(resolve, delay));
			}
		}
	}

	throw lastError;
}

/**
 * Refreshes the access token using the refresh API endpoint
 * Backend uses cookies for auth, so tokens are automatically sent
 */
export async function authRefreshToken(): Promise<Response> {
	try {
		// Get current refresh token from whichever store holds the session (sessionStorage
		// for ephemeral, localStorage for Remember Me).
		const refreshToken = getAuthItem('refreshToken');

		if (!refreshToken) {
			console.error('[Auth] No refresh token available');
			throw new Error('No refresh token available');
		}

		// Call the API refresh endpoint with retry logic
		const response = await retryWithBackoff(() => apiClient.refreshToken());

		if (!response.ok || !response.data) {
			console.error('[Auth] Refresh token failed - invalid response');
			throw new Error('Refresh token failed');
		}

		const data = response.data;

		// Validate response
		if (!data.accessToken) {
			console.error('[Auth] Invalid refresh response: missing accessToken');
			throw new Error('Invalid refresh response: missing accessToken');
		}

		// Store new tokens in the same persistence class as the existing session, so the
		// user's original Remember Me choice survives token rotation.
		const persistentRefresh = isPersistentSession();
		setAuthItem('accessToken', data.accessToken, persistentRefresh);

		// Update stored user info if provided
		if (data.user) {
			// Extract role from JWT token for consistency with login flow
			let userRole = null;
			try {
				const payload = JSON.parse(atob(data.accessToken.split('.')[1]));
				// .NET's JwtSecurityTokenHandler maps ClaimTypes.Role to the short "role" key in the JWT payload
				userRole =
					payload['role'] ||
					payload['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'] ||
					'User';
			} catch (error) {
				console.error('Failed to extract role from refreshed JWT:', error);
				userRole = 'User'; // Default role
			}

			const userWithRole = {
				...data.user,
				// JWT role claim is a string when the user holds one role and an array when
				// they hold several. Normalize to a flat string[] so role checks work either way.
				role: Array.isArray(userRole) ? userRole : [userRole],
				data: {
					...data.user,
					photoURL: 'assets/images/avatars/default-avatar.svg',
					displayName: data.user.userName || 'User',
					email: data.user.userName || ''
				}
			};

			setAuthItem('loggedUser', JSON.stringify(userWithRole), persistentRefresh);

			// Return as Response for compatibility with existing code
			return new Response(
				JSON.stringify({
					user: userWithRole,
					access_token: data.accessToken
				}),
				{
					status: 200,
					headers: { 'Content-Type': 'application/json' }
				}
			);
		}

		// If no user data in response, create minimal response
		return new Response(
			JSON.stringify({
				access_token: data.accessToken
			}),
			{
				status: 200,
				headers: { 'Content-Type': 'application/json' }
			}
		);
	} catch (error: unknown) {
		console.error('[Auth] Token refresh failed:', error);

		// Clear invalid tokens
		clearAuthStorage();

		return new Response(
			JSON.stringify({
				error: error instanceof Error ? error.message : 'Token refresh failed'
			}),
			{
				status: 401,
				headers: { 'Content-Type': 'application/json' }
			}
		);
	}
}

/**
 * Sign in with token - use stored user data with role extracted from token
 */
export async function authSignInWithToken(accessToken: string): Promise<Response> {
	try {
		// Extract role from JWT token
		// .NET's JwtSecurityTokenHandler maps ClaimTypes.Role to the short "role" key in the JWT payload
		let userRole = null;
		try {
			const payload = JSON.parse(atob(accessToken.split('.')[1]));
			userRole =
				payload['role'] || payload['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'] || 'User';
		} catch (error) {
			console.error('Failed to extract role from stored JWT:', error);
			userRole = 'User'; // Default role
		}

		// Get stored user data (from sessionStorage first, falling back to localStorage)
		const storedUser = getAuthItem('loggedUser');

		if (storedUser) {
			const userData = JSON.parse(storedUser);
			const userWithRole = {
				...userData,
				role: Array.isArray(userRole) ? userRole : [userRole],
				displayName: userData.userName,
				email: userData.userName // Assuming userName is email
			};

			return new Response(JSON.stringify(userWithRole), {
				status: 200,
				headers: { 'Content-Type': 'application/json' }
			});
		}

		throw new Error('No user data available');
	} catch (error: unknown) {
		return new Response(
			JSON.stringify({
				error: error instanceof Error ? error.message : 'Unknown error'
			}),
			{
				status: 401,
				headers: { 'Content-Type': 'application/json' }
			}
		);
	}
}

/**
 * Sign in using the fresh API client
 */
export async function authSignIn(credentials: {
	userName: string;
	password: string;
	rememberMe?: boolean;
}): Promise<Response> {
	try {
		const response = await apiClient.login({
			userName: credentials.userName,
			password: credentials.password,
			rememberMe: credentials.rememberMe ?? false
		});

		// Extract data from HttpResponse
		const data = response.data;

		// Extract role from JWT token
		let userRole = null;

		if (data.accessToken) {
			try {
				// Decode JWT to extract role information
				// .NET's JwtSecurityTokenHandler maps ClaimTypes.Role to the short "role" key in the JWT payload
				const payload = JSON.parse(atob(data.accessToken.split('.')[1]));
				userRole =
					payload['role'] ||
					payload['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'] ||
					'User';
			} catch (error) {
				console.error('Failed to extract role from JWT:', error);
				userRole = 'User'; // Default role
			}
		}

		// "Remember me" decides where tokens live: localStorage (persists across browser
		// sessions) vs sessionStorage (cleared on browser close). The store choice mirrors
		// the X-Remember-Me cookie set by the backend so the two layers stay in sync.
		const persistent = credentials.rememberMe ?? false;

		if (data.accessToken) {
			setAuthItem('accessToken', data.accessToken, persistent);
		}

		if (data.refreshToken) {
			setAuthItem('refreshToken', data.refreshToken, persistent);
		}

		if (data.user) {
			setAuthItem('loggedUser', JSON.stringify(data.user), persistent);
		}

		// Return the expected format for JWT provider: { user: User; access_token: string }
		const sessionData = {
			user: {
				uid: data.user?.id || 'user-id',
				role: Array.isArray(userRole) ? userRole : [userRole],
				data: {
					...data.user,
					photoURL: 'assets/images/avatars/default-avatar.svg',
					displayName: data.user?.userName || 'User',
					email: data.user?.userName || '' // Assuming userName is email
				}
			},
			access_token: data.accessToken
		};

		return new Response(JSON.stringify(sessionData), {
			status: 200,
			headers: { 'Content-Type': 'application/json' }
		});
	} catch (error: unknown) {
		console.error('Login error:', error);
		// Preserve the real HTTP status so the caller can tell a rejected
		// credential (4xx) apart from a system error (5xx / network). A network
		// failure carries no status — surface it as 503 so it reads as a system
		// error rather than being mistaken for bad credentials.
		const status = getErrorStatus(error) ?? 503;
		return new Response(
			JSON.stringify({
				error: error instanceof Error ? error.message : 'Login failed'
			}),
			{
				status,
				headers: { 'Content-Type': 'application/json' }
			}
		);
	}
}

/**
 * Sign up - Not implemented for Dorat
 */
export async function authSignUp(_data: {
	displayName: string;
	userName: string;
	password: string;
}): Promise<Response> {
	throw new Error('Sign up is not available in Dorat');
}

/**
 * Update user in database
 */
export async function authUpdateDbUser(user: PartialDeep<User>): Promise<Response> {
	try {
		// For now, return the user as-is since user update is not yet implemented in the API
		return new Response(JSON.stringify(user), {
			status: 200,
			headers: { 'Content-Type': 'application/json' }
		});
	} catch (error: unknown) {
		return new Response(
			JSON.stringify({
				error: error instanceof Error ? error.message : 'Unknown error'
			}),
			{
				status: 500,
				headers: { 'Content-Type': 'application/json' }
			}
		);
	}
}
